import type { JSONContent } from '@tiptap/core'
import DOMPurify from 'isomorphic-dompurify'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'

const MENTION_TOKEN_RE = /<@(\d+):([^>]+)>/g

export const emptyDoc = (): JSONContent => ({
  type: 'doc',
  content: [{ type: 'paragraph' }],
})

export const escapeAttr = (value: string): string =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')

const serializeMarks = (
  text: string,
  marks: NonNullable<JSONContent['marks']> | undefined,
): string => {
  let string = DOMPurify.sanitize(text)
  if (!string.trim()) return string

  const types = new Set((marks ?? []).map(m => m.type))
  if (types.has('underline')) string = `<u>${string}</u>`
  if (types.has('italic')) string = `<em>${string}</em>`
  if (types.has('bold')) string = `<strong>${string}</strong>`

  const linkMark = (marks ?? []).find(m => m.type === 'link')
  if (linkMark) {
    const href = escapeAttr(String(linkMark.attrs?.href ?? ''))
    string = `<a href="${href}" class="text-decoration-line: underline text-text-link" target="_blank">${string}</a>`
  }

  return string
}

const serializeInlineNode = (node: JSONContent): string => {
  if (node.type === 'text') {
    return serializeMarks(node.text ?? '', node.marks)
  }

  if (node.type === 'mention') {
    const id = node.attrs?.id
    const label = String(node.attrs?.label ?? '')
    if (!label.trim()) return ''
    return `<@${id}:${label}>`
  }

  if (node.type === 'hardBreak') {
    return '<br>'
  }

  return ''
}

const serializeInlineContent = (nodes: JSONContent[] | undefined): string => {
  if (!nodes?.length) return ''
  return nodes.map(node => serializeInlineNode(node)).join('')
}

const serializeNode = (node: JSONContent): string => {
  switch (node.type) {
    case 'doc': {
      return (node.content ?? []).map(child => serializeNode(child)).join('')
    }
    case 'paragraph': {
      const children = serializeInlineContent(node.content)
      return children.trim() ? `<p>${children}</p>` : '<br>'
    }
    case 'bulletList': {
      const children = (node.content ?? []).map(child => serializeNode(child)).join('')
      return children.trim() ? `<ul class="list-disc ps-5">${children}</ul>` : ''
    }
    case 'orderedList': {
      const children = (node.content ?? []).map(child => serializeNode(child)).join('')
      return children.trim() ? `<ol class="list-decimal ps-5">${children}</ol>` : ''
    }
    case 'listItem': {
      const children = (node.content ?? [])
        .map(child => {
          if (child.type === 'paragraph') {
            return serializeInlineContent(child.content)
          }
          return serializeNode(child)
        })
        .join('')
      return children.trim() ? `<li>${children}</li>` : ''
    }
    case 'image': {
      const src = escapeAttr(String(node.attrs?.src ?? ''))
      return `<img src="${src}"></img>`
    }
    default: {
      return (node.content ?? []).map(child => serializeNode(child)).join('')
    }
  }
}

export const docToHtml = (doc: JSONContent): string => {
  if (!doc) return '<br>'
  const html = serializeNode(doc)
  return html || '<br>'
}

export type MarkBag = { type: string; attrs?: { href: string } }

const textNode = (text: string, marks: MarkBag[]): JSONContent => {
  const node: JSONContent = { type: 'text', text }
  if (marks.length > 0) {
    node.marks = marks.map(m => (m.attrs ? { type: m.type, attrs: m.attrs } : { type: m.type }))
  }
  return node
}

export const parseTextWithMentions = (
  textContent: string,
  mentions: MentionInterface[],
  marks: MarkBag[],
): JSONContent[] => {
  const children: JSONContent[] = []
  let lastIndex = 0
  MENTION_TOKEN_RE.lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = MENTION_TOKEN_RE.exec(textContent)) !== null) {
    if (match.index > lastIndex) {
      children.push(textNode(textContent.slice(lastIndex, match.index), marks))
    }

    const userId = Number(match[1])
    const displayName = match[2]
    const mentionData = mentions.find(m => m.userId === userId && m.displayName === displayName)
    children.push({
      type: 'mention',
      attrs: {
        id: userId,
        label: displayName,
        profileImageUrl: mentionData?.profileImage ?? '',
      },
    })

    lastIndex = MENTION_TOKEN_RE.lastIndex
  }

  if (lastIndex < textContent.length) {
    children.push(textNode(textContent.slice(lastIndex), marks))
  }

  if (children.length === 0 && textContent.length === 0) {
    children.push(textNode('', marks))
  }

  return children
}

const deserializeElement = (
  el: Node,
  mentions: MentionInterface[],
  inheritedMarks: MarkBag[] = [],
): JSONContent[] => {
  if (el.nodeType === Node.TEXT_NODE) {
    return parseTextWithMentions(el.textContent || '', mentions, inheritedMarks)
  }

  if (!(el instanceof Element)) {
    return []
  }

  const marks = [...inheritedMarks]

  switch (el.nodeName) {
    case 'STRONG':
    case 'B': {
      marks.push({ type: 'bold' })
      break
    }
    case 'EM':
    case 'I': {
      marks.push({ type: 'italic' })
      break
    }
    case 'U': {
      marks.push({ type: 'underline' })
      break
    }
    case 'A': {
      marks.push({
        type: 'link',
        attrs: { href: el.getAttribute('href') ?? '' },
      })
      break
    }
  }

  const childNodes = [...el.childNodes].flatMap(child => deserializeElement(child, mentions, marks))

  switch (el.nodeName) {
    case 'P': {
      return [{ type: 'paragraph', content: childNodes.length > 0 ? childNodes : undefined }]
    }
    case 'BR': {
      return [{ type: 'paragraph' }]
    }
    case 'UL': {
      return [{ type: 'bulletList', content: childNodes.filter(n => n.type === 'listItem') }]
    }
    case 'OL': {
      return [{ type: 'orderedList', content: childNodes.filter(n => n.type === 'listItem') }]
    }
    case 'LI': {
      const hasBlock = childNodes.some(
        n => n.type === 'paragraph' || n.type === 'bulletList' || n.type === 'orderedList',
      )
      return [
        {
          type: 'listItem',
          content: hasBlock
            ? childNodes
            : [{ type: 'paragraph', content: childNodes.length > 0 ? childNodes : undefined }],
        },
      ]
    }
    case 'IMG': {
      return [{ type: 'image', attrs: { src: el.getAttribute('src') ?? '' } }]
    }
    default: {
      return childNodes
    }
  }
}

export const htmlToDoc = (html: string, mentions: MentionInterface[] = []): JSONContent => {
  if (!('DOMParser' in globalThis)) {
    return emptyDoc()
  }

  const parsed = new DOMParser().parseFromString(html || '', 'text/html')
  const bodyChildren = deserializeElement(parsed.body, mentions)

  if (bodyChildren.length === 0) {
    return emptyDoc()
  }

  const content: JSONContent[] = []
  let pendingNodes: JSONContent[] = []

  const flushPending = (): void => {
    if (pendingNodes.length === 0) return
    content.push({
      type: 'paragraph',
      content: pendingNodes,
    })
    pendingNodes = []
  }

  for (const node of bodyChildren) {
    if (
      node.type === 'paragraph' ||
      node.type === 'bulletList' ||
      node.type === 'orderedList' ||
      node.type === 'image'
    ) {
      flushPending()
      content.push(node)
    } else {
      pendingNodes.push(node)
    }
  }
  flushPending()

  if (content.length === 0) {
    return emptyDoc()
  }

  return { type: 'doc', content }
}
